import React, { useCallback, useEffect, useState } from "react";
import { Search, CalendarDays, CircleX, Loader, ClipboardList, Plus, UserRoundPlus } from "lucide-react";
import { formatDate, numberWithCommas, exchangeDate, debounce, getSpeciality, can_visit } from "../../helpers/index.js";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { notify, getPractice } from "../../helpers/index.js";
import { deleteBulkClinicalNoteData, deleteClinicalNote, fetchClinicalNotes } from "../../services/clinicalNotes.js";
import DataTablePagination from "../DataTablePagination.jsx";
import Spacing from "../Spacing.jsx";
import DatePicker from "react-datepicker";
import ClinicalNotesDataTable from "../DataTable/ClinicalNotesDataTable.jsx";
import ClinicalNoteImportModal from "../Modal/ClinicalNoteImportModal.jsx";
import BulkDeleteModal from "../Modal/BulkDeleteModal.jsx";
import AddClinicalPatientModal from "../Modal/AddClinicalPatientModal.jsx";

export default function ClinicalNotes({ selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit, setLetterLimit, changePracticeSpeciality, showPrevisitLoaderData }) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let is_provider = getPractice("is_provider") || false;
  let specilityId = getSpeciality()?.id || 32;
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specilityId = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 20; }

  const [buttonLoader, setButtonLoader] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [clinicalNoteData, setClinicalNoteData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [dateRange, setDateRange] = useState(["", ""]);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    start_date: "",
    end_date: "",
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    order_by: [],
    provider_id: is_provider ? user_id : null
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [clinicalNoteImport, setClinicalNoteImport] = useState(false);
  const [importLoader, setImportLoader] = useState(false);
  const [clinicalNoteBulkDelete, setClinicalNoteBulkDelete] = useState(false);
  const [clinicalNoteAdd, setClinicalNoteAdd] = useState(false);

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  useEffect(() => {
    loadClinicalNoteData(filter);
  }, [filter]);

  // Loads the waiting room data based on the provided filter.
  const loadClinicalNoteData = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchClinicalNotes(filter);
      setClinicalNoteData(response?.data?.results || []);
      setPaginationCount({
        totalCount: response?.data?.count,
        totalPages: response?.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadClinicalNoteData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  // Handles the change of rows per page in the filter.
  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  // Handles the page change event.
  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  // Handles the change event for the search term input field.
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  // Handle single date change
  const handleDateChange = (update) => {
    const [start, end] = update;

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    }
    setDataLoading(true)

    setDateRange([start, end]);
  };

  // Handles the checkbox change event for a specific clinicalNoteData.
  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setClinicalNoteData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
      } else {
        updatedCheckedIds.add(id);
      }
      return updatedCheckedIds;
    });
  };

  // Handles the select all functionality for clinicalNoteData.
  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
    }

    setSelectAll(!selectAll);

    setClinicalNoteData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  // Deletes an clinicalNoteData record.
  const handleDeleteRecord = async (id) => {
    setDataLoading(true);
    try {
      const response = await deleteClinicalNote(id, account_id, location_id);

      if (response) {
        if (response?.detail) {
          notify('error', response?.detail);
        } else { 
          if(response?.data?.status === 'success'){
            notify("success", response?.data?.message);
            await loadClinicalNoteData(filter);
          } else {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const channel = new BroadcastChannel('tab_practice_change');
  useEffect(() => {
    channel.onmessage = (event) => {
      setFilter({
        ...filter,
        location_id: event?.data?.location_id,
        account_id: event?.data?.account_id,
      });
      location_id = event?.data?.location_id;
      account_id = event?.data?.account_id;
      setCheckedIds([]);
    };
    return () => {
      channel.close();
    };
  }, [channel, location_id, account_id]);

  const handleDownloadSelect = async (e) => {
    setButtonLoader(true);
    try {
      const ids = checkedIds;

      ids.forEach(id => {
        if (!isNaN(id) && id !== null && id !== undefined) {

          const matchingWaiting = clinicalNoteData.find((item) => item.id === id);
          if (matchingWaiting) {
            const url = `/previsit?waiting_id=${id}&type=download`;
            // window.open(url, '_blank');
            window.open(url, id, 'height=100,width=100,left=1000,top=1000');
          }
  
        } else {
          console.error('Invalid ID encountered:', id);
        }
      });
      
    } catch (error) {
      // console.log("🚀 ~ DownloadSelect ~ error:", error);
    } finally {
      setTimeout(() => {
        setButtonLoader(false);
      }, 7000);
    }
  };

  const handleBulkDeleteRecord = async (e) => {
    setDataLoading(true);
    try {
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        selected_id: checkedIds,
      };

      const response = await deleteBulkClinicalNoteData(deleteData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.success){
            notify("success", response?.data?.success, 0.5);
            await loadClinicalNoteData(filter);
          } else {
            notify("error", response?.data?.error);
          }
        }
      }
    } catch (error) {
      // console.log("🚀 ~ handleBulkDeleteRecord ~ error:", error);
    } finally {
      setDataLoading(false);
    }
  };

  useDocumentTitle("Clinical Notes");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24" style={{ marginBottom: "0px" }}>
          <i className="docpro_blue_bg docpro_white_color">
            <ClipboardList width={20} height={20} />
          </i>
          Clinical Notes
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit('clinical_notes') && can_visit('clinical_notes_create')) && (
            <button
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn docpro_sm_btn"
              onClick={() => {
                setClinicalNoteAdd(!clinicalNoteAdd);
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <UserRoundPlus width={20} height={20} />
              <span className="text">Add Patient</span>
            </button>
          )}
        </div>
      </div>
      <Spacing lg="5" sm="5" />

      <div className="docpro_data_table_wrap">
        <div className={`docpro_data_table_headings small_heading docpro_grid_gap_10 ${filter.start_date === filter.end_date ? "docpro_have_date_filter" : "" }`}>
          <div className={`searching_div docpro_grid_gap_10 ${(filter.start_date && (filter.start_date === filter.end_date) && (paginationCount.totalCount > 0) && specilityId) ? "docpro_sm_height_lg_42" : ""}`}>
            <form className="docpro_search_form">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            <div className={`flex items-center docpro_grid_gap_4 docpro_date_filter`}>
              <div className={`docpro_toggle_calendar ${filter.start_date ? "active" : "" }`}>
                <CalendarDays />

                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                {/* <DatePicker
                  selected={
                    filter.start_date ? new Date(filter.start_date) : new Date()
                  }
                  onChange={(date) => handleDateChange(date)}
                /> */}
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}
              </div>
              {filter.start_date && (
                <span onClick={() => {
                  setFilter({
                    ...filter,
                    start_date: "",
                    end_date: "",
                  });
                  setDateRange([null, null]);
                  setDataLoading(true);
                }}>
                  <CircleX size={22} color="var(--accent)" className="pointer" />
                </span>
              )}
            </div>
          </div>

          <div className="docpro_data_table_btns p-0">
            {(can_visit('clinical_notes') && can_visit('clinical_notes_import')) && (
              <button 
                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                onClick={() => {
                  setClinicalNoteImport(!clinicalNoteImport);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
              >
                <Plus width={20} height={20} />
                <span>Import</span>
                {importLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        <ClinicalNotesDataTable
          tableData={clinicalNoteData}
          handleSelectAll={() => handleSelectAll(clinicalNoteData)}
          handleCheckboxChange={handleCheckboxChange}
          handleDeleteRecord={handleDeleteRecord}
          reloadData={loadClinicalNoteData}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          checkSelected={checkSelected}
          downloadBulkData={handleDownloadSelect}
          buttonLoader={buttonLoader}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          clinicalNoteBulkDelete={clinicalNoteBulkDelete}
          setClinicalNoteBulkDelete={setClinicalNoteBulkDelete}
        />
        {(clinicalNoteData.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {clinicalNoteImport && (
        <ClinicalNoteImportModal
          account_id={account_id}
          location_id={location_id}
          reloadData={loadClinicalNoteData}
          reloadDataFilter={filter}
          clinicalNoteImport={clinicalNoteImport}
          setClinicalNoteImport={setClinicalNoteImport}
          previousScrollPosition={previousScrollPosition}
          setImportLoader={setImportLoader}
        />
      )}

      {clinicalNoteBulkDelete && (
        <BulkDeleteModal
          handleDelete={() => handleBulkDeleteRecord()}
          name={`${checkedIds?.length} records`}
          activeModal={clinicalNoteBulkDelete}
          setActiveModal={setClinicalNoteBulkDelete}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {clinicalNoteAdd && (
        <AddClinicalPatientModal
          reloadData={loadClinicalNoteData}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          clinicalNoteAdd={clinicalNoteAdd}
          setClinicalNoteAdd={setClinicalNoteAdd}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
